import {Grammars, IToken, Parser, TokenError} from 'ebnf';
import { bnf } from './bnf/cotlang.ebnf';
import {COTLangExec} from "./COTLangExec";

const parser = new Grammars.W3C.Parser(bnf());

export class COTLangParser extends COTLangExec {
    parser: Parser;

    constructor(environment: { [key: string]: string }, baseURL: string | null, authorization: string | null) {
        super(environment, baseURL, authorization);
        this.parser = parser;
    }

    public parseOnly(input: string) {
        return this.parser.getAST(String(input));
    }

    public async resolve(input: any, value: any, outputs: any[], callback?: (err: Error | null, data: any) => void) {
        let copy;
        try {
            copy = { reference: JSON.parse(JSON.stringify(input)) };
        } catch {
            // cannot copy, return original
            return input;
        }

        try {
            await this.resolveInPlace(copy, value, outputs);
        } catch (e) {
            if (callback) return callback(e, copy);
            else throw e;
        }
        if (callback) return callback(null, copy.reference);
        else return copy.reference;
    }

    private async resolveInPlace(input: any, value: any, outputs: any[], callback?: (err: Error | null, data: any) => void) {
        try {
            if (Array.isArray(input)) {
                for (let i = 0; i < input.length; i += 1) {
                    input[i] = await this.resolveInPlace(input[i], value, outputs);
                }
            } else if (typeof input === 'object' && input !== null) {
                const keys = Object.keys(input);
                for (let i = 0; i < keys.length; i += 1) {
                    input[keys[i]] = await this.resolveInPlace(input[keys[i]], value, outputs);
                }
            } else if (typeof input === 'string') {
                return await this.parse(input, value, outputs);
            }
        } catch (e) {
            if (callback) return callback(e, input);
            else throw e;
        }

        if (callback) return callback(null, input);
        else return input;
    }

    private safeStringify(input: any) {
        try {
            return JSON.stringify(input);
        } catch (e) {
            return String(input);
        }
    }


    private buildErrorMessage(input: string, e: Error, value: any, outputs: any[]) {
        if (e instanceof TokenError) {
            const text = input || e.token.text || ' ';
            const message = e.message || '';
            const segmentA = e.token.start;
            const segmentB = e.token.end - e.token.start - 1;
            const space = (length: number, fill: string) => Array(length).fill(fill).join('');
            const arrowHead = `${space(segmentA, ' ')}↑${space(segmentB, ' ')}↑`;
            const arrowLine = `${space(segmentA, '─')}┴${space(segmentB, '─')}┘`;
            return `${text}\n${arrowHead}\n${arrowLine}\n\n${message}`;
        }

        console.log(`IMPORTANT: This in an unexpected error.

Please submit a ticket to Edward <edward@cotalker.com> with the following content:

=================================================
Input: ${input}
-------------------------------------------------
Value: ${this.safeStringify(value)}
-------------------------------------------------
Output: ${this.safeStringify(outputs)}
-------------------------------------------------
Error: ${String(e)}

${e.stack}
=================================================
`);
        return String(e);
    }

    private async parse(input: string, value: any, outputs: any[]) {
        if (input === null || input === undefined || input === '') return input;

        const ast = this.parseOnly(String(input));
        if (!ast) {
            // This is an expected error when expression cannot be resolved.
            throw new Error(`Cannot resolve expression ${input}`);
        }
        if (ast.errors.length) {
            // This is a non-expected error.
            // Please report.
            throw new Error(this.buildErrorMessage(input, ast.errors[0], value, outputs));
        }

        try {
            return await this.exec(ast, value, outputs);
        } catch (e) {
            // This is a run time non-expected error.
            // Please report.
            throw new Error(this.buildErrorMessage(input, e, value, outputs));
        }
    }
}
